import type { JobStage } from "@sts/schemas";

/**
 * The pipeline's sixteen stages folded into seven colour bands, for the jobs
 * map.
 *
 * Sixteen colours on a map cannot be told apart; seven can, and each band is
 * a question an office manager actually asks of a map ("where are my open
 * surveys?", "what is installed round here?"). A `Record` over the stage
 * union, so a stage added to `@sts/schemas` that nobody has put in a band is
 * a compile error rather than a grey dot.
 *
 * The colours are distinct in hue AND lightness, so they still separate for
 * the commonest colour-vision deficiencies and in greyscale; the band's name
 * travels with every pin as its tooltip and in the list, so colour is never
 * the only carrier of the stage.
 */

export type StageBand =
  | "enquiry"
  | "survey"
  | "design"
  | "won"
  | "installed"
  | "handed-over"
  | "closed";

export const STAGE_BAND: Record<JobStage, StageBand> = {
  lead: "enquiry",
  qualified: "enquiry",
  "estimate-sent": "enquiry",
  "survey-booked": "survey",
  surveyed: "survey",
  designed: "design",
  "proposal-sent": "design",
  signed: "won",
  scheduled: "won",
  installed: "installed",
  commissioned: "installed",
  certified: "installed",
  "bus-redeemed": "installed",
  "handover-complete": "handed-over",
  lost: "closed",
  cancelled: "closed",
};

export const BAND_ORDER: readonly StageBand[] = [
  "enquiry",
  "survey",
  "design",
  "won",
  "installed",
  "handed-over",
  "closed",
];

export const BAND_LABEL: Record<StageBand, string> = {
  enquiry: "Enquiry",
  survey: "Survey",
  design: "Design & proposal",
  won: "Signed & scheduled",
  installed: "Installed",
  "handed-over": "Handed over",
  closed: "Lost or cancelled",
};

export const BAND_COLOUR: Record<StageBand, string> = {
  enquiry: "#5b6b7c",
  survey: "#d97706",
  design: "#7c3aed",
  won: "#1d6fd8",
  installed: "#15803d",
  "handed-over": "#0f4c3a",
  closed: "#a3a3a3",
};
